import { Link, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtMWh, fmtNum } from "@/lib/format";
import { STATUS } from "@/lib/status";
import { cn } from "@/lib/utils";

export const PLANT_ORDER = { stopped: 0, maintenance: 1, reduced: 2, running: 3 };

/** Sort: deviations first (stopped, maintenance, reduced), then plants with active events, then by name. */
export const sortDeviationsFirst = (plants = []) =>
  [...plants].sort((a, b) => PLANT_ORDER[a.status] - PLANT_ORDER[b.status] || (b.events_open || 0) - (a.events_open || 0) || a.name.localeCompare(b.name, "nb"));

export const plantShortName = (name = "") => name.replace(/ kraftverk$/i, "");

/* Normal operation is quiet: «I drift» as plain text. Deviations are clear: a coloured dot, the word, and the reason. */
const REASON_PREFIX = { reduced: /^redusert drift\s*[–\-:]\s*/i, stopped: /^(stanset|stans)\s*[–\-:]\s*/i, maintenance: /^planlagt vedlikehold$/i };
/** The reason without repeating the status word («Redusert · Redusert drift – …» → «Redusert · delvis tett inntaksrist»). */
export const plantReason = (p) => {
  if (!p.status_reason) return null;
  if (p.status === "maintenance" && REASON_PREFIX.maintenance.test(p.status_reason)) return "planlagt";
  const re = REASON_PREFIX[p.status];
  return re ? p.status_reason.replace(re, "") : p.status_reason;
};

const StatusCell = ({ p }) => {
  const cfg = STATUS[p.status] || STATUS.running;
  const deviation = p.status !== "running";
  if (!deviation) return <span className="text-[14.5px] text-muted-foreground">{cfg.label}</span>;
  const reason = plantReason(p);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className={cn("status-dot", `status-dot-${p.status}`)} aria-hidden="true" />
      <span className={cn("truncate text-[14.5px] font-medium", cfg.text)}>
        {cfg.label}
        {reason ? <span className="font-normal text-foreground"> · {reason}</span> : null}
      </span>
    </span>
  );
};

const PowerCell = ({ p }) => {
  const d = (p.capacity_mw || 0) < 1 ? 2 : 1;
  return (
    <span className="metric whitespace-nowrap text-[14.5px]">
      <span className={cn("font-medium", p.status === "stopped" ? "text-rose-700 dark:text-rose-300" : "text-foreground")}>{fmtNum(p.power_mw, d)}</span>
      <span className="text-muted-foreground"> / {fmtNum(p.capacity_mw, d)} MW</span>
    </span>
  );
};

/** Utilisation as a plain percentage – the many near-identical bars said little. */
const UtilCell = ({ p }) => <span className={cn("metric text-[14.5px]", p.status === "running" ? "text-foreground" : "text-muted-foreground")}>{fmtNum(p.utilisation_pct, 0)} %</span>;

/** Consistent decimals in the column (fmtMWh switches between 0 and 1 decimals, which breaks alignment). */
const energyCell = (v) => (v === null || v === undefined ? "–" : Math.abs(v) >= 10000 ? `${fmtNum(v / 1000, 1)} GWh` : `${fmtNum(v, 1)} MWh`);

/* The count is coloured only while something is actually going on at the plant. An event that has ended and just
   waits for acknowledgement is a plain number – the plant is running again, and the row should not shout. */
export const eventsTone = (p) => {
  const n = p.events_open || 0;
  if (!n) return null;
  const ongoing = p.events_ongoing ?? (p.status !== "running" ? n : 0);
  if (ongoing) return p.events_critical ? "text-rose-700 dark:text-rose-300" : "text-amber-700 dark:text-amber-300";
  return "text-foreground";
};
export const eventsTitle = (p) => {
  const n = p.events_open || 0;
  if (!n) return undefined;
  const ongoing = p.events_ongoing ?? 0;
  const unack = p.events_unacknowledged ?? 0;
  if (ongoing) return ongoing === 1 ? "1 hendelse pågår" : `${ongoing} hendelser pågår`;
  if (unack) return unack === 1 ? "1 avsluttet hendelse venter på kvittering" : `${unack} avsluttede hendelser venter på kvittering`;
  return n === 1 ? "1 kvittert hendelse venter på lukking" : `${n} kvitterte hendelser venter på lukking`;
};

const EventsCell = ({ p }) => {
  const n = p.events_open || 0;
  if (!n) return <span className="text-muted-foreground/50">–</span>;
  return (
    <span className={cn("metric text-[14.5px] font-semibold", eventsTone(p))} title={eventsTitle(p)} data-testid="plant-active-events">
      {n}
    </span>
  );
};

/**
 * Plant table – a register of comparable data, on Oversikt and Kraftverk.
 * Columns: Kraftverk | Effekt nå | Utnyttelse | Produsert i dag | Status | Hendelser. No outer frame, hairlines between rows;
 * the whole row opens the plant.
 */
export const PlantTable = ({ plants = [], loading, limit, showMunicipality = false, emptyText = "Ingen kraftverk", className, testId = "plant-table" }) => {
  const navigate = useNavigate();
  const rows = limit ? plants.slice(0, limit) : plants;
  const th = "py-2.5 font-medium";
  return (
    <div className={cn("min-w-0", className)} data-testid={testId}>
      {/* Desktop / tablet */}
      <table className="hidden w-full border-collapse text-[14.5px] sm:table">
        <thead>
          <tr className="border-b border-border text-left text-[13px] text-muted-foreground">
            <th className={cn(th, "w-[26%] pr-4")}>Kraftverk</th>
            <th className={cn(th, "w-[15%] pr-4 text-right")}>Effekt nå</th>
            <th className={cn(th, "w-[11%] pr-4 text-right")}>Utnyttelse</th>
            <th className={cn(th, "hidden w-[14%] pr-4 text-right md:table-cell")}>Produsert i dag</th>
            <th className={cn(th, "pr-4")}>Status</th>
            <th className={cn(th, "w-[10%] text-right")}>Hendelser</th>
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-border">
                  <td colSpan={6} className="py-3">
                    <Skeleton className="h-6" />
                  </td>
                </tr>
              ))
            : rows.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/kraftverk/${p.id}`)}
                  className="group cursor-pointer border-b border-border transition-[background-color] duration-100 hover:bg-muted/60"
                  data-testid={`plant-row-${p.id}`}
                >
                  <td className="py-3 pr-4 align-middle">
                    <Link to={`/kraftverk/${p.id}`} onClick={(e) => e.stopPropagation()} className="text-[15px] font-semibold leading-snug text-foreground group-hover:text-primary" data-testid={`plant-link-${p.id}`}>
                      {plantShortName(p.name)}
                    </Link>
                    {showMunicipality && p.municipality ? <span className="ml-2 text-[13px] text-muted-foreground">{p.municipality}</span> : null}
                  </td>
                  <td className="py-3 pr-4 text-right align-middle">
                    <PowerCell p={p} />
                  </td>
                  <td className="py-3 pr-4 text-right align-middle">
                    <UtilCell p={p} />
                  </td>
                  <td className="metric hidden py-3 pr-4 text-right align-middle text-[14.5px] md:table-cell">{energyCell(p.energy_today_mwh)}</td>
                  <td className="max-w-0 py-3 pr-4 align-middle">
                    <StatusCell p={p} />
                  </td>
                  <td className="py-3 text-right align-middle">
                    <EventsCell p={p} />
                  </td>
                </tr>
              ))}
        </tbody>
      </table>

      {/* Mobile list */}
      <ul className="divide-y divide-border sm:hidden">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="py-3">
                <Skeleton className="h-12" />
              </li>
            ))
          : rows.map((p) => (
              <li key={p.id}>
                <Link to={`/kraftverk/${p.id}`} className="flex items-start justify-between gap-3 py-3" data-testid={`plant-row-mobile-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold leading-snug">{plantShortName(p.name)}</div>
                    <div className="mt-1">
                      <StatusCell p={p} />
                    </div>
                    {showMunicipality && p.municipality ? <div className="mt-0.5 text-[13px] text-muted-foreground">{p.municipality}</div> : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <PowerCell p={p} />
                    <div className="metric mt-0.5 text-[13px] text-muted-foreground">{fmtMWh(p.energy_today_mwh)} i dag</div>
                    {p.events_open ? <div className={cn("mt-0.5 text-[13px] font-medium", eventsTone(p))}>{p.events_open} {p.events_open === 1 ? "hendelse" : "hendelser"}{p.events_ongoing ? "" : " · avsluttet"}</div> : null}
                  </div>
                </Link>
              </li>
            ))}
      </ul>
      {!loading && !rows.length ? <p className="py-8 text-[14.5px] text-muted-foreground">{emptyText}</p> : null}
    </div>
  );
};
